/**
 * CreateHoldedInvoiceModal - Modal para crear facturas en Holded
 *
 * Un concepto (line_type) por factura. Si es «other», cada línea lleva cuenta Holded;
 * si no, la cuenta viene del config y no se envía account en las líneas.
 */

import { faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@shared/components/ui/button';
import { TagsInput } from '@shared/components/ui/TagsInput';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Card, CardContent } from '@shared/components/ui/shadcn/card';
import { DynamicCombobox } from '@shared/components/ui/DynamicCombobox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { ScrollArea } from '@shared/components/ui/shadcn/scroll-area';
import { Separator } from '@shared/components/ui/shadcn/separator';
import { TextArea } from '@shared/components/ui/textarea';
import { formatCurrencyAmount, useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { forwardRef, useImperativeHandle, useRef, useState, useCallback } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  CreateHoldedInvoiceData,
  HOLDED_LINE_DESC_MAX_LENGTH,
  HOLDED_LINE_TYPES,
  HoldedInvoiceLinePayload,
  HoldedLineType,
  useCreateHoldedInvoice,
} from '../../hooks/useCreateHoldedInvoice';
import { useInvoiceModalTeamsCombobox } from '../../hooks/useInvoiceModalTeamsCombobox';

export interface CreateHoldedInvoiceModalRef {
  open: (onSuccess?: () => void) => void;
}

interface HoldedInvoiceLineForm {
  desc: string;
  units: number;
  subtotal: number;
  account: string;
  tags: string[];
}

interface HoldedInvoiceFormData {
  team_id: string;
  line_type: HoldedLineType;
  notes: string;
  lines: HoldedInvoiceLineForm[];
}

export const CreateHoldedInvoiceModal = forwardRef<CreateHoldedInvoiceModalRef>((_, ref) => {
  const { t } = useTranslation('invoicesModule');
  const currency = useCurrency();
  const modalRef = useRef<ModalCenterRef>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {
    searchFn: teamSearchFn,
    onLoadMore: loadMoreTeams,
    hasMore: hasMoreTeams,
    isLoadingBrowse: isLoadingTeamsBrowse,
  } = useInvoiceModalTeamsCombobox({ enabled: isModalOpen });

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);
  const { createHoldedInvoice, isCreating } = useCreateHoldedInvoice();
  const [onSuccessCallback, setOnSuccessCallback] = useState<(() => void) | null>(null);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch,
    reset,
    clearErrors,
    getValues,
  } = useForm<HoldedInvoiceFormData>({
    defaultValues: {
      team_id: '',
      line_type: 'customer_service',
      notes: '',
      lines: [{ desc: '', units: 1, subtotal: 0, account: '', tags: [] }],
    },
  });

  const { fields, append, remove } = useFieldArray<HoldedInvoiceFormData, 'lines'>({
    control,
    name: 'lines',
  });

  const lineType = watch('line_type');

  useImperativeHandle(ref, () => ({
    open: (onSuccess?: () => void) => {
      setOnSuccessCallback(() => onSuccess || null);
      setIsModalOpen(true);
      reset({
        team_id: '',
        line_type: 'customer_service',
        notes: '',
        lines: [{ desc: '', units: 1, subtotal: 0, account: '', tags: [] }],
      });
      modalRef.current?.openModal();
    },
  }));

  const addLine = () => {
    append({ desc: '', units: 1, subtotal: 0, account: '', tags: [] });
  };

  const removeLine = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  const calculateLineTotal = (units: number, subtotal: number): number => {
    return units * subtotal;
  };

  const calculateTotal = (): number => {
    return fields.reduce((total, _, index) => {
      const lineData = watch(`lines.${index}` as const);
      if (lineData) {
        return total + calculateLineTotal(lineData.units || 0, lineData.subtotal || 0);
      }
      return total;
    }, 0);
  };

  const onSubmit = async (data: HoldedInvoiceFormData) => {
    const processedLines: HoldedInvoiceLinePayload[] = data.lines.map((line) => {
      const payload: HoldedInvoiceLinePayload = {
        units: line.units,
        subtotal: line.subtotal,
        desc: line.desc.trim(),
      };
      if (line.tags?.length) payload.tags = line.tags;
      if (data.line_type === 'other') {
        payload.account = line.account?.trim() ?? '';
      }
      return payload;
    });

    const invoiceData: CreateHoldedInvoiceData = {
      team_id: parseInt(data.team_id, 10),
      line_type: data.line_type,
      lines: processedLines,
      currency: 'EUR',
      notes: data.notes?.trim() || undefined,
    };

    const result = await createHoldedInvoice(invoiceData);

    if (result) {
      onSuccessCallback?.();
      modalRef.current?.closeModal();
    }
  };

  const handleCancel = () => {
    modalRef.current?.closeModal();
  };

  return (
    <ModalCenter
      ref={modalRef}
      title={t('modals.create_holded_invoice.title', 'Crear Factura en Holded')}
      type="default"
      width="xxl"
      fullScreen
      className="overflow-hidden"
      classNameBody="flex flex-col min-h-0 overflow-hidden p-6 pt-4"
      onCloseModal={handleCloseModal}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col min-h-0 flex-1 overflow-hidden">
        {/* Cliente y concepto en la misma fila */}
        <div className="flex-shrink-0 space-y-3">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:items-start">
            <div className="space-y-2">
              <Label htmlFor="team_id" className="text-sm text-muted-foreground">
                {t('modals.create_holded_invoice.client', 'Cliente')} *
              </Label>
              <Controller
                name="team_id"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <DynamicCombobox
                    value={field.value}
                    setValue={field.onChange}
                    placeholder={
                      isLoadingTeamsBrowse
                        ? t('common.loading', 'Cargando...')
                        : t('modals.create_holded_invoice.select_client', 'Seleccionar cliente')
                    }
                    searchFn={teamSearchFn}
                    onLoadMore={loadMoreTeams}
                    hasMore={hasMoreTeams}
                    disabled={isLoadingTeamsBrowse}
                    dataTestId="create-holded-invoice-client-combobox"
                    emptyMessage={t('modals.create_holded_invoice.no_clients_found', 'No se encontraron clientes')}
                  />
                )}
              />
              {errors.team_id && (
                <p className="text-xs text-destructive mt-1">{t('validation.client_required', 'Cliente requerido')}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">
                {t('modals.create_holded_invoice.invoice_concept', 'Concepto de la factura')} *
              </Label>
              <Controller
                name="line_type"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={(v) => {
                      field.onChange(v);
                      if (v !== 'other') {
                        (getValues('lines') ?? []).forEach((_, i) => {
                          setValue(`lines.${i}.account`, '');
                          clearErrors(`lines.${i}.account`);
                        });
                      }
                    }}
                  >
                    <SelectTrigger className="h-10 text-sm">
                      <SelectValue
                        placeholder={t('modals.create_holded_invoice.select_line_type', 'Seleccionar tipo')}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {HOLDED_LINE_TYPES.map((option) => (
                        <SelectItem key={option} value={option}>
                          {t(`modals.create_holded_invoice.line_type_options.${option}`, option)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.line_type && (
                <p className="text-xs text-destructive">
                  {t('validation.line_type_required', 'Selecciona un concepto')}
                </p>
              )}
            </div>
          </div>
          <p className="text-xs text-muted-foreground">{t('modals.create_holded_invoice.invoice_concept_help')}</p>
        </div>

        <Separator className="my-5" />

        {/* Líneas */}
        <div className="flex-shrink-0 flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-foreground">
            {t('modals.create_holded_invoice.lines', 'Líneas de factura')}
          </h3>
          <Button type="button" variant="outline" size="sm" onClick={addLine} className="h-8">
            <FontAwesomeIcon icon={faPlus} className="mr-2 size-3.5" />
            {t('modals.create_holded_invoice.add_line', 'Añadir línea')}
          </Button>
        </div>

        <div className="flex-1 min-h-0 overflow-hidden">
          <div className="flex items-center gap-3 py-2 px-1 border-b border-border font-medium text-xs text-muted-foreground">
            <span className="min-w-[160px] flex-1">{t('modals.create_holded_invoice.line_desc', 'Descripción')} *</span>
            <span className="w-20 shrink-0">{t('modals.create_holded_invoice.units_short', 'Ud')}</span>
            <span className="w-32 shrink-0">{t('modals.create_holded_invoice.subtotal', 'Subtotal')}</span>
            {lineType === 'other' && (
              <span className="min-w-[130px] shrink-0">
                {t('modals.create_holded_invoice.holded_account', 'Cuenta Holded')} *
              </span>
            )}
            <span className="min-w-[180px] flex-[2]">{t('modals.create_holded_invoice.tags', 'Etiquetas extra')}</span>
            {fields.length > 1 && <span className="w-9 shrink-0" />}
          </div>
          <ScrollArea className="h-[calc(100%-2.5rem)] -mx-1 px-1">
            <div className="pr-3">
              {fields.map((_, index) => {
                const descRegister = register(`lines.${index}.desc` as const, {
                  validate: (value) => {
                    const s = String(value ?? '').trim();
                    if (!s) {
                      return t('validation.line_desc_required');
                    }
                    if (s.length > HOLDED_LINE_DESC_MAX_LENGTH) {
                      return t('validation.line_desc_max', {
                        max: HOLDED_LINE_DESC_MAX_LENGTH,
                      });
                    }
                    return true;
                  },
                });
                const subtotalRegister = register(`lines.${index}.subtotal` as const, {
                  required: true,
                  min: 0,
                  valueAsNumber: true,
                });
                return (
                  <div
                    key={`line-${index}`}
                    className="flex items-center gap-3 py-2 border-b border-border/60 last:border-0"
                  >
                    <div className="min-w-[160px] flex-1">
                      <Input
                        className="h-9 text-sm w-full"
                        maxLength={HOLDED_LINE_DESC_MAX_LENGTH}
                        {...descRegister}
                        onBlur={(e) => {
                          descRegister.onBlur(e);
                          const trimmed = e.target.value.trim();
                          if (trimmed !== e.target.value) {
                            setValue(`lines.${index}.desc`, trimmed, {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                          }
                        }}
                        placeholder={t('modals.create_holded_invoice.line_desc_placeholder', 'Descripción')}
                      />
                      {errors.lines?.[index]?.desc && (
                        <p className="text-xs text-destructive mt-0.5">{errors.lines?.[index]?.desc?.message}</p>
                      )}
                    </div>
                    <div className="w-20 shrink-0">
                      <Input
                        type="number"
                        step="1"
                        min={0}
                        className="h-9 text-sm"
                        selectAllOnFocus={false}
                        {...register(`lines.${index}.units` as const, {
                          required: true,
                          min: 0,
                          valueAsNumber: true,
                        })}
                      />
                    </div>
                    <div className="w-32 shrink-0">
                      <Input
                        type="number"
                        step="0.01"
                        min={0}
                        className="h-9 text-sm"
                        selectAllOnFocus={false}
                        {...subtotalRegister}
                        placeholder="0.00"
                        onBlur={(e) => {
                          subtotalRegister.onBlur(e);
                          const v = parseFloat(e.target.value);
                          if (!Number.isNaN(v)) {
                            setValue(`lines.${index}.subtotal`, Number(v.toFixed(2)), {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                          }
                        }}
                      />
                    </div>
                    {lineType === 'other' && (
                      <div className="min-w-[130px] shrink-0">
                        <Input
                          className="h-9 text-sm font-mono"
                          {...register(`lines.${index}.account` as const, {
                            validate: (value, formValues) => {
                              if (formValues.line_type === 'other' && !String(value || '').trim()) {
                                return t('validation.holded_account_required');
                              }
                              return true;
                            },
                          })}
                          placeholder={t('modals.create_holded_invoice.holded_account_placeholder', 'Id cuenta')}
                        />
                        {errors.lines?.[index]?.account && (
                          <p className="text-xs text-destructive mt-0.5">{errors.lines?.[index]?.account?.message}</p>
                        )}
                      </div>
                    )}
                    <div className="min-w-[180px] flex-[2]">
                      <Controller
                        name={`lines.${index}.tags` as const}
                        control={control}
                        render={({ field }) => (
                          <TagsInput
                            value={field.value}
                            onChange={field.onChange}
                            placeholder={t('modals.create_holded_invoice.tags_placeholder')}
                          />
                        )}
                      />
                    </div>
                    {fields.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeLine(index)}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10 h-9 w-9 shrink-0 p-0"
                      >
                        <FontAwesomeIcon icon={faTrash} className="size-3.5" />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </div>

        <Separator className="my-5" />

        <div className="flex-shrink-0 space-y-4">
          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="p-4">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-muted-foreground">
                  {t('modals.create_holded_invoice.grand_total', 'Total general')}
                </span>
                <span className="text-xl font-semibold text-foreground">
                  {formatCurrencyAmount(calculateTotal(), currency)}
                </span>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-2">
            <Label htmlFor="notes" className="text-sm text-muted-foreground">
              {t('modals.create_holded_invoice.notes', 'Notas')}
            </Label>
            <TextArea
              id="notes"
              {...register('notes')}
              placeholder={t('modals.create_holded_invoice.notes_placeholder', 'Notas adicionales para la factura')}
              rows={2}
              className="resize-none"
            />
          </div>
        </div>

        <div className="flex-shrink-0 flex justify-end gap-3 pt-6 mt-4 border-t border-border">
          <Button type="button" variant="outline" onClick={handleCancel} disabled={isCreating}>
            {t('modals.create_holded_invoice.cancel', 'Cancelar')}
          </Button>
          <Button type="submit" disabled={isCreating}>
            {isCreating
              ? t('modals.create_holded_invoice.creating', 'Creando...')
              : t('modals.create_holded_invoice.create', 'Crear Factura en Holded')}
          </Button>
        </div>
      </form>
    </ModalCenter>
  );
});

CreateHoldedInvoiceModal.displayName = 'CreateHoldedInvoiceModal';
